import Vue from 'vue'
import Echo from 'laravel-echo'
import { eventBusUtility } from '@/eventBusUtility.js'
import network from './network'
import Dexie from 'dexie'
import * as Sentry from '@sentry/vue'
import { safeLocalStorageSet } from '../../services/local-storage-safe'
import { cacheableProfile } from '../../services/profile-cache'

const state = {
	status: false,
	profile: {},
	translations: {},
	userdatas: {},
	permissions: [],
	role: {},
	settings: [],
	tenant: {},
	uuid: '',
	pusher: {},
	notifications: [],
	notificationsCount: 0,
	notificationsStatus: false,
	notificationsArchive: [],
	notificationsArchiveCount: 0,
	yellologsCount: 0,
	avatars: {},
	users: {},
	notificationSnackbars: [],
	notificationSnackbarId: 0,
}

// Costanti modulo-level usate da canActOn (estratte per evitare riallocazione ad ogni call).
// Fallback workflow: action di cambio stato ereditano da 'edit', audit/tfr da 'show'.
// Replica HrBasePolicy::WORKFLOW_ACTION_FALLBACK lato backend.
const WORKFLOW_FALLBACK = {
	approve: 'edit', reject: 'edit', cancel: 'edit', reopen: 'edit',
	reimburse: 'edit', terminate: 'edit', compile: 'edit',
	audit: 'show', tfr: 'show',
}
// Guard self-approve: replica HrAbsencePolicy::approve / HrExpensePolicy::approve guard.
// Approve/Reject su un record che appartiene a me stesso non e' mai permesso,
// anche se ho scope=all. Cancel resta consentito (annullo la mia stessa richiesta).
const SELF_BLOCKED_ACTIONS = ['approve', 'reject']

// Cache memoizzata per canActOn keyed sull'oggetto state.role (riferimento).
// Quando state.role viene rimpiazzato (USER_SUCCESS), il vecchio oggetto viene
// raccolto e la sua entry nella WeakMap rimossa automaticamente. La cache
// secondaria e' una Map<string, boolean> con chiave 'submodule|action|itemId'.
const canActCache = new WeakMap()

function computeCanAct(role, profile, submodule, action, item) {
	const toInt = v => (v == null || v === '' ? null : Number(v))
	const subPerms = role && role.permissions
		? (role.permissions[submodule] || {})
		: {}
	let perm = subPerms[action]
	if ((perm === null || perm === undefined) && WORKFLOW_FALLBACK[action]) {
		perm = subPerms[WORKFLOW_FALLBACK[action]]
	}
	if (perm === null || perm === undefined || perm === false || perm === 'false') return false
	if (!item) {
		if (perm === true || perm === 'true' || perm === 'all') return true
		return true
	}
	const prof = profile || {}
	const me = toInt(prof.hr_employee_id)
	const itemEmp = toInt(submodule === 'hremployees' ? item.id : item.employee_id)
	if (SELF_BLOCKED_ACTIONS.includes(action) && me != null && itemEmp === me) return false
	if (perm === true || perm === 'true' || perm === 'all') return true
	const itemDept = toInt(item.department_id !== undefined && item.department_id !== null
		? item.department_id
		: item.employee_department_id)
	if (perm === 'self') return itemEmp != null && itemEmp === me
	if (perm === 'direct_reports')
		return Array.isArray(prof.hr_direct_report_ids)
			&& prof.hr_direct_report_ids.map(Number).includes(itemEmp)
	if (perm === 'department')
		return Array.isArray(prof.hr_managed_department_ids)
			&& prof.hr_managed_department_ids.map(Number).includes(itemDept)
	if (perm === 'department_tree')
		return Array.isArray(prof.hr_managed_department_tree_ids)
			&& prof.hr_managed_department_tree_ids.map(Number).includes(itemDept)
	return false
}

const getters = {
	getProfile: state => state.profile,

	getOfflineActive: state => !!state.profile.offline,

	offlineMode: state => {
		return !!state.profile.offline && !network.state.online
	},

	getUserId: state => state.profile.id,

	uuid: state => state.uuid,

	pusher: state => state.pusher,

	isProfileLoaded: state => !!state.profile.name,

	getPermissions: state => state.permissions,

	getTenant: state => state.tenant || {},

	isMobileEnabled: state => !!(state.tenant && state.tenant.mobile_enabled),

	isPecMassiveEnabled: state => !!(state.tenant && state.tenant.pec_massive_enabled),

	// Modalità "solo HR" (flag sul tenant o sul ruolo, già incrociato col modulo Hr
	// dal backend): dashboard su I miei dati e menu tutto al primo livello
	isHrOnly: state => !!(state.profile && state.profile.hr_only),

	isMefImportAnyDocument: state => !!(state.tenant && state.tenant.mef_import_any_document),

	status: state => state.status,

	avatars: state => state.avatars,

	users: state => state.users,

	translations: state => state.translations,

	userdatas: state => state.userdatas,

	usersSeq: state => {
		return Object.keys(state.users).map(i => state.users[i])
	},

	yellologsCount: state => state.yellologsCount,

	notifications: state => state.notifications,

	notificationsCount: state => state.notificationsCount,

	notificationsArchive: state => state.notificationsArchive,

	notificationsArchiveCount: state => state.notificationsArchiveCount,

	notificationsStatus: state => state.notificationsStatus,

	notificationSnackbars: state => state.notificationSnackbars,

	hasApiPermission: (state, getters) => {
		return getters.checkSettings('api_permission') && state.profile.open_api
	},

	checkSettings: (state) => (key) => {
		let resultIndex = state.settings.findIndex(setting => setting.key === key)
		if (resultIndex >= 0) {
			return state.settings[resultIndex].value === true
		} else {
			return false
		}
	},
	
	getSetting: (state) => (key) => {
		let resultIndex = state.settings.findIndex(setting => setting.key === key)
		if (resultIndex >= 0) {
			return state.settings[resultIndex].value
		} else {
			return false
		}
	},

	getSettingByModel: (state) => (model, key) => {
		// Find the setting for a specific model and key
		// Settings in the database have separate 'model' and 'key' fields
		let resultIndex = state.settings.findIndex(setting =>
			setting.model === model && setting.key === key
		)
		if (resultIndex >= 0) {
			return state.settings[resultIndex].value
		} else {
			return undefined
		}
	},

	checkModelPermissions: (state) => (key) => {
	    // for custom models
	    let resultIndexModel = state.permissions.findIndex(permission => permission.model === key)
	    if (resultIndexModel >= 0) {
	        return true
	    } else {
	        return false
	    }
	},

	checkModulePermissions: (state) => (key) => {
		// for custom modules
		if(!key) {
			return true
		}

		let check = false
		// manage 'or' case for multiple modules
		key.split('|').every(module => {
			if (state.permissions.findIndex(permission => permission.module == module) != -1) {
				check = true
				return false // stop loop when found
			}

			return true // return to continue the loop
		})
		return check
	},
	
	/**
	 * Verifica se l'utente può eseguire un'azione su un item specifico,
	 * applicando i 5 livelli di scope HR (false/self/direct_reports/
	 * department/department_tree/all) + alias 'true'='all'.
	 *
	 * @param {string} submodule       chiave permission (es. 'hrabsences')
	 * @param {string} action          azione (es. 'show', 'approve', 'edit')
	 * @param {object|null} [item]     record da valutare; null per index/create
	 * @returns {boolean}
	 */
	canActOn: (state) => (submodule, action, item = null) => {
		const role = state.role
		if (!role || typeof role !== 'object') {
			return computeCanAct(role, state.profile, submodule, action, item)
		}
		let subCache = canActCache.get(role)
		if (!subCache) {
			subCache = new Map()
			canActCache.set(role, subCache)
		}
		const itemId = item ? (item.id != null ? item.id : 'noid') : 'null'
		const key = submodule + '|' + action + '|' + itemId
		if (subCache.has(key)) return subCache.get(key)
		const result = computeCanAct(role, state.profile, submodule, action, item)
		subCache.set(key, result)
		return result
	},

	checkRolePermissions: (state) => (key, action, value = true) => {
		if (state.role && state.role.permissions && state.role.permissions[key]) {
			const permission = state.role.permissions[key][action]
			
			// Handle boolean values for comparison
			if (typeof value === 'boolean') {
				// If permission is boolean, compare directly
				if (typeof permission === 'boolean') {
					return permission === value
				}
				// If permission is string, convert to boolean for comparison
				if (permission === 'true') return value === true
				if (permission === 'false') return value === false
				// Values like 'owner', 'group' should not match boolean values
				return false
			}
			
			// When comparing with string values like 'owner', 'group'
			if (typeof value === 'string' && value !== 'true' && value !== 'false') {
				return permission === value
			}
			
			// For 'true'/'false' string values
			if (value === 'true') {
				return permission === true || permission === 'true'
			}
			if (value === 'false') {
				return permission === false || permission === 'false'
			}
			
			// Fallback to original behavior
			return permission == value
		}
		return false
	},
	
	checkUserdata: (state) => (key, value = true, operator = "==") => {
		// Find the userdata with the provided key
		const userdata = state.userdatas.find(data => data.key === key);
		
		if (!userdata) {
			return false;
		}
		
			// Get the userdata value, ensuring proper type conversion
		let userdataValue = userdata.value;
		
		// Convert boolean-like strings to actual booleans for comparison
		if (userdataValue === "true") userdataValue = true;
		if (userdataValue === "false") userdataValue = false;
		
		// Try to convert to number if possible
		const numValue = Number(userdataValue);
		if (!isNaN(numValue) && userdataValue !== "" && userdataValue !== true && userdataValue !== false) {
			userdataValue = numValue;
		}
		
		// Perform comparison based on operator
		switch (operator) {
			case "==":
			case "===":
				return userdataValue == value;
			case "!=":
			case "!==":
				return userdataValue != value;
			case ">":
				return userdataValue > value;
			case ">=":
				return userdataValue >= value;
			case "<":
				return userdataValue < value;
			case "<=":
				return userdataValue <= value;
			case "in":
				return Array.isArray(value) && value.includes(userdataValue);
			case "!in":
				return Array.isArray(value) && !value.includes(userdataValue);
			default:
				return userdataValue == value; // Default to equality
		}
	},
	
	getUserdata: (state) => (key) => {
		const userdata = state.userdatas.find(data => data.key === key);
		return userdata ? userdata.value : null;
	},
	
	getTempestIframeUrl: (state, getters) => {
		// Get the base URL template
		const urlTemplate = getters.getUserdata('tempest_url')
		
		if (!urlTemplate) {
			return ''
		}
		
		// Get all required values from userdata
		const username = getters.getUserdata('tempest_username')
		const alias = getters.getUserdata('tempest_alias')
		const password = getters.getUserdata('tempest_password')
		const phoneNumber = getters.getUserdata('tempest_phone_number')
		
		// Ensure required values exist
		if (!username || !alias || !password) {
			return ''
		}
		
		// Replace placeholders with actual values
		let url = urlTemplate
			.replace('{{test}}@{{stiipbot}}', `${username}@${alias}`)
			.replace('{{test}}', password)
		
		// Handle phone number - if missing, remove the parameter
		if (phoneNumber) {
			url = url.replace('{{200}}', phoneNumber)
		} else {
			url = url.replace(/&phone_num=\{\{200\}\}/, '')
		}

		// Parametro opzionale: aggiunto solo se l'utente ha la userdata
		const auto_answer = getters.getUserdata('auto_answer')
		if (auto_answer != null && auto_answer !== '') {
			url += `&auto_answer=${encodeURIComponent(auto_answer)}`
		}

		// Stato dell'operatore all'apertura della barra (es. READY), anche
		// dopo un refresh: opzionale e per utente, come auto_answer, così si
		// sceglie su chi attivarlo. La barra non comunica i cambi di stato,
		// quindi con READY anche chi era in pausa riparte pronto.
		const start_op_status = getters.getUserdata('start_op_status')
		if (start_op_status != null && start_op_status !== '') {
			url += `&start_op_status=${encodeURIComponent(start_op_status)}`
		}

		return url
	},

	/*
	 * URL dei due componenti della barra: la toolbar in alto e la finestra di
	 * gestione chiamata. Tempest li serve dallo stesso indirizzo distinguendoli
	 * con `iframe`, e richiede `parent_origin` per poter inviare messaggi alla
	 * pagina che li ospita: senza, il browser li scarta perche' GMH sta su un
	 * dominio diverso dal loro. L'origine va comunicata a Tempest, che la
	 * accetta solo se presente nella loro configurazione.
	 */
	getTempestSectionUrl: (state, getters) => (section) => {
		const base = getters.getTempestIframeUrl

		if (!base) {
			return ''
		}

		try {
			const url = new URL(base)
			url.searchParams.set('iframe', section)
			url.searchParams.set('parent_origin', window.location.origin)
			return url.toString()
		} catch (e) {
			return ''
		}
	},

	getTempestToolbarUrl: (state, getters) => getters.getTempestSectionUrl('main_toolbar'),

	getTempestChatSectionUrl: (state, getters) => getters.getTempestSectionUrl('chat_section')
}

const actions = {
	USER_REQUEST: ({commit, dispatch, rootGetters}) => {
		return new Promise((resolve, reject) => {

			Vue.axios({url: 'user-details', method: 'GET' })
			.then(resp => {
				commit('USER_REQUEST')
				// La copia in cache del profilo serve solo al fallback offline: se
				// la quota è piena si prosegue lo stesso, l'app legge il profilo
				// dallo store. Prima era un'assegnazione secca e la
				// QuotaExceededError finiva nel catch qui sotto, che la scambiava
				// per errore di autenticazione e faceva logout subito dopo il login.
				// In cache va la versione senza immagini base64: lo store in memoria
				// riceve comunque il payload completo, avatar inclusi.
				safeLocalStorageSet('user', JSON.stringify(cacheableProfile(resp.data.data)))

				commit('USER_SUCCESS', resp.data.data)

				// Registra l'utente su OneSignal usando la nuova API 5.x
				if(rootGetters.isMobile && Vue.prototype.$oneSignal) {
					// OneSignal 5.x usa login() invece di setExternalUserId()
					Vue.prototype.$oneSignal.login(resp.data.data.uuid + '_' + resp.data.data.user.id)
					// Imposta anche l'email se disponibile
					if (resp.data.data.user.email) {
						Vue.prototype.$oneSignal.setEmail(resp.data.data.user.email)
					}
				}

				/** START LARAVEL ECHO CONNECTION */
				// Se lo script socket.io non e' stato caricato (server socket giu' o
				// irraggiungibile) new Echo lancia un'eccezione: senza questo try il
				// catch qui sotto la scambiava per un errore di autenticazione e
				// faceva logout subito dopo il login. Le notifiche in tempo reale
				// restano spente, l'app e' comunque utilizzabile.
				try {
				Vue.prototype.$echo = new Echo({
					broadcaster: 'socket.io',
					host: rootGetters.baseUrl,
					withCredentials: true,
					auth: {
						headers: {
							Authorization: 'Bearer ' + rootGetters.token
						}
					}
				})

				Vue.prototype.$echo.connector.socket.on('connect', () => {
					commit('SET_NOTIFICATIONS_STATUS', true)
				});

				Vue.prototype.$echo.private('notification.'+state.uuid+'.'+state.profile.id)
				.notification((notification) => {
					eventBusUtility.webNotify(notification)
					commit('PUSH_NOTIFICATION_SNACKBAR', notification)

					Vue.axios.post('notifications/', {filters: {status: 'unread'}, page: 1, per_page: 10, exclude_archives: true}).then(response => {
						commit('SET_NOTIFICATIONS', response.data)
					}, response => {
						// error callback
					})
					Vue.axios.post('notifications/', {filters: {status: 'unread'}, page: 1, per_page: 10, only_archives: true}).then(response => {
						commit('SET_NOTIFICATIONS_ARCHIVE', response.data)
					}, response => {
						// error callback
					})
					//this.$eventBusUtility.$emit('updateNotifications')
				})
				// .listen('.ChannelSubscribed', () => {
				// 	console.log('notification sub')
					// commit('SET_NOTIFICATIONS_STATUS', true)
				// })
				// .on('subscription_error', () => {
				// 	commit('SET_NOTIFICATIONS_STATUS', false)
				// })
				
				// connect to yellologs channel
				if(rootGetters.checkModelPermissions('yellolog')) {
					Vue.prototype.$echo.private('yellologs.'+state.uuid)
					.listen('.Yellologs', (e) => {

						Vue.axios.get('yellologscount/').then(response => {
							// get body data
							commit('SET_YELLOLOGS_COUNT', response.data.data)
						}, response => {
							// error callback
						})
					})
					// .on('subscription_succeeded', () => {
					// 	console.log('collegato a yellologs')
					// })
					// .on('echo:subscription_error', () => {
					// 	console.log('non collegato a yellologs')
					// })
				}

				Vue.prototype.$echo.private('refresh')
				.listen('.Refresh', (e) => {
					window.location.reload(true)
				})
				
				Vue.prototype.$echo.private('refreshDB')
				.listen('.Refresh', (e) => {
					// Static method
					localStorage.removeItem('last_sync_' + localStorage.fqdn)
					Dexie.delete('YelloDB_' + localStorage.fqdn).then(() => {
						dispatch('syncDB', null, { root: true })
					})
				})
				} catch (err) {
					console.warn('Notifiche in tempo reale non disponibili', err)
					commit('SET_NOTIFICATIONS_STATUS', false)
				}
				/** END LARAVEL ECHO */

				dispatch('syncDB', null, { root: true })

				eventBusUtility.$emit('loggedIn')

				resolve(true)
			})
			.catch(resp => {
				const status = resp && resp.response ? resp.response.status : null

				if(((resp && resp.message == "Network Error") || status === 503) && localStorage.user){
					// Profilo ripristinato dalla cache: l'app è utilizzabile offline,
					// quindi la action è andata a buon fine e non deve far fallire
					// il login o il boot che la stanno aspettando.
					commit('USER_SUCCESS', JSON.parse(localStorage.user))
					resolve(true)
					return
				}

				commit('USER_ERROR')

				if(status === 401 || status === 403) {
					// Rifiuto esplicito del backend: logout completo, con la POST
					// che invalida il token anche lato server.
					dispatch('AUTH_LOGOUT')
				} else {
					// Qualsiasi altro errore (incluse le eccezioni lanciate dal .then
					// qui sopra) non dice nulla sulla validità del token, quindi
					// niente chiamata di logout. La sessione locale va però azzerata:
					// senza profilo l'app è inutilizzabile e con isAuthenticated
					// ancora true i guard che aspettano isProfileLoaded (whenUserReady)
					// resterebbero appesi.
					commit('AUTH_LOGOUT')
					// Solo le eccezioni vere finiscono su Sentry: un errore di rete
					// (che porta comunque .request) è normale in mobilità.
					if(!resp || (!resp.response && !resp.request)) {
						Sentry.captureException(resp)
					}
				}
				// Con reject() nudo l'errore spariva: in console restava solo
				// "Uncaught (in promise) undefined" e Sentry non vedeva niente.
				reject(resp)
			})
		})
	},
}

const mutations = {
	USER_REQUEST: (state) => {
		state.status = false
	},

	USER_SUCCESS: (state, resp) => {
		state.profile = resp.user
		state.uuid = resp.uuid
		state.permissions = resp.permissions
		state.role = resp.role
		state.settings = resp.settings
		state.tenant = resp.tenant || {}
		state.translations = resp.translations
		state.userdatas = resp.userdatas
		state.yellologsCount = resp.yellologs
		state.users = resp.users
		state.status = true
	},

	SET_NOTIFICATIONS: (state, data) => {
		state.notifications = data.notifications.data
		state.notificationsCount = data.notifications.total
		state.avatars = data.avatars
	},

	SET_NOTIFICATIONS_ARCHIVE: (state, data) => {
		state.notificationsArchive = data.notifications.data
		state.notificationsArchiveCount = data.notifications.total
	},

	REMOVE_NOTIFICATION: (state, notificationId) => {
		state.notifications = state.notifications.filter(n => n.id !== notificationId)
		if (state.notificationsCount > 0) state.notificationsCount--
	},

	SET_NOTIFICATIONS_STATUS: (state, data) => {
		state.notificationsStatus = data
	},
	
	SET_YELLOLOGS_COUNT: (state, data) => {
		state.yellologsCount = data
	},

	PUSH_NOTIFICATION_SNACKBAR: (state, notification) => {
		state.notificationSnackbarId++
		state.notificationSnackbars.push({
			id: state.notificationSnackbarId,
			...notification
		})
	},

	REMOVE_NOTIFICATION_SNACKBAR: (state, id) => {
		state.notificationSnackbars = state.notificationSnackbars.filter(n => n.id !== id)
	},

	USER_ERROR: (state) => {
		state.status = 'error'
	},
	
	AUTH_LOGOUT: (state) => {
		state.profile = {}
		delete localStorage.user
	},

	SET_SETTINGS: (state, settings) => {
		state.settings = settings
	},

	SET_TENANT_FLAG: (state, { key, value }) => {
		state.tenant = { ...state.tenant, [key]: value }
	},
}

export default {
	state,
	getters,
	actions,
	mutations,
}